import {
  BadgeMinus,
  ChevronRight,
  DatabaseZap,
  LogOut,
  ShieldAlert,
  UserRoundX,
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useVoxnela } from '../../contexts/VoxnelaContext'
import {
  openSubscriptionManagement,
  startDeletionProtocol,
  type DeletionProtocolResult,
} from '../../lib/accountLifecycle'
import { Modal } from '../ui/Modal'
import { useUiText } from '../../i18n/ui'
import { authMessages, formatAuthMessage, type AccountAction } from '../../i18n/auth'

type ProfileAction = AccountAction

function profileErrorMessage(cause: unknown, fallback: string): string {
  const message = cause instanceof Error ? cause.message : ''
  return message || fallback
}

function formatDeletionDate(value: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Istanbul',
  }).format(new Date(value))
}

export function AccountProfileActions({
  onSessionEnded,
}: {
  onSessionEnded?: () => void
}) {
  const { locale } = useUiText()
  const copy = authMessages[locale]
  const { user, isGuest, logout } = useAuth()
  const { clearLocalUserData } = useVoxnela()
  const [profileAction, setProfileAction] = useState<ProfileAction | null>(null)
  const [profileActionWorking, setProfileActionWorking] = useState(false)
  const [profileActionError, setProfileActionError] = useState('')
  const [logoutWorking, setLogoutWorking] = useState(false)
  const [deletionResult, setDeletionResult] =
    useState<DeletionProtocolResult | null>(null)
  const selectedActionCopy = profileAction
    ? copy.actions[profileAction]
    : null

  if (!user) return null

  const chooseProfileAction = (action: ProfileAction) => {
    setProfileAction(action)
    setProfileActionError('')
    setDeletionResult(null)
  }

  const dismissProfileAction = () => {
    if (profileActionWorking) return
    const sessionEnded = deletionResult !== null
    setProfileAction(null)
    setProfileActionError('')
    setDeletionResult(null)
    if (sessionEnded) onSessionEnded?.()
  }

  const confirmProfileAction = async () => {
    if (!profileAction || profileActionWorking) return
    setProfileActionWorking(true)
    setProfileActionError('')
    try {
      if (profileAction === 'subscription') {
        await openSubscriptionManagement(user.uid)
        setProfileAction(null)
        return
      }

      const result = await startDeletionProtocol(profileAction)
      setDeletionResult(result)
      clearLocalUserData()
      try {
        await logout()
      } catch (logoutError) {
        console.warn(
          '[VOXNELA_ACCOUNT] Silme protokolü oluşturuldu; yerel oturum kapatma tamamlanamadı.',
          logoutError,
        )
      }
    } catch (cause) {
      setProfileActionError(
        profileErrorMessage(
          cause,
          copy.operationFailed,
        ),
      )
    } finally {
      setProfileActionWorking(false)
    }
  }

  const signOut = async () => {
    if (logoutWorking) return
    setLogoutWorking(true)
    try {
      await logout()
      onSessionEnded?.()
    } catch (cause) {
      console.error('[VOXNELA_AUTH] Çıkış işlemi tamamlanamadı.', cause)
    } finally {
      setLogoutWorking(false)
    }
  }

  return (
    <>
      <div className="auth-account account-profile-actions">
        <div className="account-action-list" aria-label={copy.accountActions}>
          <button
            type="button"
            className="account-action-row"
            onClick={() => chooseProfileAction('subscription')}
          >
            <span className="account-action-icon">
              <BadgeMinus size={18} />
            </span>
            <span>
              <strong>{copy.manageSubscriptionTitle}</strong>
              <small>{copy.manageSubscriptionSubtitle}</small>
            </span>
            <ChevronRight size={17} />
          </button>
          <button
            type="button"
            className="account-action-row account-action-row--danger"
            onClick={() => chooseProfileAction('account')}
          >
            <span className="account-action-icon">
              <UserRoundX size={18} />
            </span>
            <span>
              <strong>{copy.deleteAccountTitle}</strong>
              <small>{copy.deleteAccountSubtitle}</small>
            </span>
            <ChevronRight size={17} />
          </button>
          <button
            type="button"
            className="account-action-row account-action-row--danger"
            onClick={() => chooseProfileAction('data')}
          >
            <span className="account-action-icon">
              <DatabaseZap size={18} />
            </span>
            <span>
              <strong>{copy.deleteDataTitle}</strong>
              <small>{copy.deleteDataSubtitle}</small>
            </span>
            <ChevronRight size={17} />
          </button>
        </div>

        {/* Signing out of a guest session would only discard this device's work. */}
        {!isGuest && (
          <button
            type="button"
            className="outline-button full-button auth-logout-button"
            disabled={logoutWorking}
            onClick={() => void signOut()}
          >
            <LogOut size={17} />
            {logoutWorking ? copy.signingOut : copy.signOut}
          </button>
        )}
      </div>

      <Modal
        open={profileAction !== null}
        onClose={dismissProfileAction}
        eyebrow={deletionResult ? copy.requestReceived : selectedActionCopy?.eyebrow}
        title={
          deletionResult
            ? deletionResult.type === 'account'
              ? copy.accountDeletionStarted
              : copy.dataDeletionStarted
            : selectedActionCopy?.title ?? copy.accountAction
        }
      >
        {deletionResult ? (
          <div className="account-action-confirmation account-action-confirmation--complete">
            <span className="account-warning-icon">
              <ShieldAlert size={24} />
            </span>
            <p>
              {deletionResult.type === 'account'
                ? formatAuthMessage(copy.accountDeletesAt, { date: formatDeletionDate(deletionResult.executeAfterMs, locale) })
                : formatAuthMessage(copy.dataDeletesAt, { date: formatDeletionDate(deletionResult.executeAfterMs, locale) })}
            </p>
            <p>
              {copy.deletionUndo}
            </p>
            <button
              type="button"
              className="primary-button full-button"
              onClick={dismissProfileAction}
            >
              {copy.done}
            </button>
          </div>
        ) : (
          <div className="account-action-confirmation">
            <span className="account-warning-icon">
              <ShieldAlert size={24} />
            </span>
            <div className="account-warning-copy">
              {selectedActionCopy?.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            {profileActionError && (
              <p className="auth-error">{profileActionError}</p>
            )}
            <div className="account-warning-buttons">
              <button
                type="button"
                className="outline-button"
                disabled={profileActionWorking}
                onClick={dismissProfileAction}
              >
                {copy.cancel}
              </button>
              <button
                type="button"
                className={
                  profileAction === 'subscription'
                    ? 'primary-button'
                    : 'danger-button'
                }
                disabled={profileActionWorking}
                onClick={() => void confirmProfileAction()}
              >
                {profileActionWorking
                  ? copy.preparing
                  : selectedActionCopy?.confirm}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
